/*
  This is a simplified AI assistant component for a new view.
*/
import { useState, useEffect } from "react";
import { CodeFile, languageType, Message } from "utils/interfaces";
import ChatMessages from "./DemoChatMessages";
import ChatMessageInput from "./DemoChatMessageInput";

interface SimpleAssistantParams {
  files?: CodeFile[] | undefined;
  AIAssistant: {
    name: string;
    img: string;
    greeting: string;
    intro: string;
  };
  language: languageType | null;
}

const MinecraftDemoAssistant: React.FC<SimpleAssistantParams> = ({
  files,
  AIAssistant,
  language,
}) => {
  // States definition
  const [waitingForAiResponse, setWaitingForAiResponse] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showLoading, setShowLoading] = useState(false);

  // Simulated AI responses for scripted conversation
  const scriptedResponses: Record<string, string> = {
    "Can you check my code?":
      "Your code looks correct so far, all you need to do now is complete Step 10 to call the function.\n\nWould you like a hint on how to do that?",
    "What is a function?":
      "A function is a reusable block of code that performs a specific task.",
    default:
      "I understand. Let me know if you have any specific questions about your code or the concepts you're learning.",
  };

  /**
   * Function used to set the timer for waiting for AI response
   */
  useEffect(() => {
    if (waitingForAiResponse) {
      const timer = setTimeout(() => {
        if (waitingForAiResponse) {
          setShowLoading(true);
        }
      }, 750);
      return () => clearTimeout(timer);
    } else {
      setShowLoading(false);
    }
  }, [waitingForAiResponse]);

  /**
   * For sending messages via the conversation starters
   * @param text
   * @returns
   */
  const handleUserMessage = async (text: string) => {
    // Prevent send the message if the text is empty
    if (!text) return;

    // Check if it's already performing a request
    if (waitingForAiResponse) return;

    // Lock the function
    setWaitingForAiResponse(true);

    // Update the messages with user message
    setMessages((prev) => [...prev, { content: text, role: "user" }]);

    // Simulate AI thinking
    const thinkingTimeout = setTimeout(() => {
      // First, hide the loading indicator
      setShowLoading(false);

      // Get the scripted response
      const response = scriptedResponses[text] || scriptedResponses["default"];

      // Wait for loading animation to finish exiting
      const animationTimeout = setTimeout(() => {
        // Add AI response to messages
        setMessages((prev) => [
          ...prev,
          { content: response, role: "assistant" },
        ]);

        // Unlock the function
        setWaitingForAiResponse(false);
      }, 700);

      // Store cleanup function in an effect
      return () => clearTimeout(animationTimeout);
    }, 2000);

    // Store cleanup function in an effect
    return () => clearTimeout(thinkingTimeout);
  };

  return (
    <div className="w-full h-full flex flex-col gap-6 z-10 items-center rounded-lg overflow-hidden">
      {/* List of messages */}
      <ChatMessages
        messages={messages}
        showLoading={showLoading}
        AIAssistant={AIAssistant}
        returnToIDE={() => {}}
        moveToNextLesson={() => {}}
        language={language}
        nameInitials="ME"
      />
      <div className="inline-flex flex-col items-center gap-4 h-fit w-full">
        {/* Chat text input */}
        <ChatMessageInput
          inputEnabled={waitingForAiResponse}
          handleSend={handleUserMessage}
        />
      </div>
    </div>
  );
};

export default MinecraftDemoAssistant;
